import { ref, type ComputedRef, type Ref, computed } from "vue";
import { BaseService, ErrorResponse } from "../base";
import type { LogItem } from "../standort/model";
import type { KennzahlenGroesse, ModalSplit, OekologischeReinigung, ProduktDl, Einsatzstoffverwendung, Recycling, Teilnahme, Produktionsrueckstandsaufkommen, Energie, Kennzahl, Anmerkung, Klimabilanz, Massnahme, Umweltwirkung, Abfallaufkommen, Heizwaermebedarf, DatenjahrItem, SdgBewertung, Stakeholder, Handlungsfelder, Lieferant, MitarbeiterStruktur, Nachhaltigkeitsprogramm, QCheck, AuditCheckliste, Dekarbonisierungsstrategie, GroessteUmweltauswirkungen, Zielgroesse, Wasserverbrauch } from "./model";
import type { NachhaltigkeitsFrage } from "../configuration/model";

export const DatenjahrServiceSymbol = Symbol();
export const LocalStorageSelectedDatenjahr = "oekoprofit_selectedDatenjahr";
export const LocalStorageSelectedTeilnahme = "oekoprofit_selectedTeilnahme";

export interface IDatenjahrItemRepository<T> {
  List(): Promise<T[]>;
  Patch(item: T): Promise<void>;
  Delete(itemid: string): Promise<void>;
  ListAll(): Promise<T[]>;
}

export class DatenjahrItemRepository<T> implements IDatenjahrItemRepository<T> {
  private _service: BaseService;
  private _slug: string;
  private _datenjahr: number;

  constructor(service: BaseService, slug: string, datenjahr: number) {
    this._service = service;
    this._slug = slug;
    this._datenjahr = datenjahr;
  }

  async List(): Promise<T[]> {
    let result: T[] | ErrorResponse = await this._service.Get<T[]>(`${this._datenjahr}/${this._slug}`, true);

    if (result instanceof ErrorResponse) {
      console.log(result.message);
      return Array<T>();
    }

    return result;
  }

  async ListAll(): Promise<T[]> {
    let result: T[] | ErrorResponse = await this._service.Get<T[]>(this._slug, true);

    if (result instanceof ErrorResponse) {
      console.log(result.message);
      return Array<T>();
    }

    return result;
  }

  async Patch(item: T): Promise<void> {
    let result: T | ErrorResponse = await this._service.Patch(`${this._datenjahr}/${this._slug}`, item);
    
    if (result instanceof ErrorResponse) {
      console.log(result.message);
    }
  }

  async Delete(itemid: string): Promise<void> {
    let result: void | ErrorResponse = await this._service.Delete(`${this._datenjahr}/${this._slug}/${itemid}`);

    if (result instanceof ErrorResponse) {
      console.log(result.message);
    }
  }
}

export interface IDatenjahrService {
  Datenjahr: Ref<number>;
  DatenjahrIsSet: ComputedRef<boolean>;
  DatenjahrIsLaufendesJahr: ComputedRef<boolean>;
  Teilnahme: Ref<Teilnahme>;
  TeilnahmeIsSet: ComputedRef<boolean>;
  SetDatenjahr(datenjahr: number, reloadPage: boolean): void;
  ClearDatenjahr(): void;
  SetTeilnahme(teilnahme: Teilnahme): void;
  ClearTeilnahme(): void;

  Anmerkungen: IDatenjahrItemRepository<Anmerkung>;

  Abfallaufkommen: IDatenjahrItemRepository<Abfallaufkommen>;
  Bezugsgroessen: IDatenjahrItemRepository<KennzahlenGroesse>;
  Kennzahlen: IDatenjahrItemRepository<Kennzahl>;
  ProdukteUndDl: IDatenjahrItemRepository<ProduktDl>;
  Einsatzstoffverwendungen: IDatenjahrItemRepository<Einsatzstoffverwendung>;
  Recycling: IDatenjahrItemRepository<Recycling>;
  Produktionsrueckstandsaufkommen: IDatenjahrItemRepository<Produktionsrueckstandsaufkommen>;
  Energie: IDatenjahrItemRepository<Energie>;
  Klimabilanz: IDatenjahrItemRepository<Klimabilanz>;
  ModalSplit: IDatenjahrItemRepository<ModalSplit>;
  OekologischeReinigung: IDatenjahrItemRepository<OekologischeReinigung>;
  Dekarbonisierungstrategien: IDatenjahrItemRepository<Dekarbonisierungsstrategie>;
  Massnahmen: IDatenjahrItemRepository<Massnahme>;
  GroessteUmweltauswirkungen: IDatenjahrItemRepository<GroessteUmweltauswirkungen>;
  Umweltwirkungen: IDatenjahrItemRepository<Umweltwirkung>;
  Heizwaermebedarfe: IDatenjahrItemRepository<Heizwaermebedarf>;
  Teilnahmen: IDatenjahrItemRepository<Teilnahme>;
  SdgBewertungen: IDatenjahrItemRepository<SdgBewertung>;
  Stakeholder: IDatenjahrItemRepository<Stakeholder>;
  Handlungsfelder: IDatenjahrItemRepository<Handlungsfelder>;
  Lieferanten: IDatenjahrItemRepository<Lieferant>;
  MitarbeiterStrukturen: IDatenjahrItemRepository<MitarbeiterStruktur>;
  Nachhaltigkeitsprogramme: IDatenjahrItemRepository<Nachhaltigkeitsprogramm>;
  QChecks: IDatenjahrItemRepository<QCheck>;
  AuditChecklisten: IDatenjahrItemRepository<AuditCheckliste>;
  Zielgroessen: IDatenjahrItemRepository<Zielgroesse>;
  Wasserverbrauch: IDatenjahrItemRepository<Wasserverbrauch>;

  Protokoll(): Promise<LogItem[]>;
}

export class DatenjahrService extends BaseService implements IDatenjahrService {
  private _datenjahr: Ref<number>;
  private _teilnahme: Ref<Teilnahme>;

  constructor(baseUri: string, token: string) {
    super(baseUri, token);

    this._datenjahr = ref<number>(null);
    if (localStorage.getItem(LocalStorageSelectedDatenjahr) != null) {
      this._datenjahr.value = JSON.parse(localStorage.getItem(LocalStorageSelectedDatenjahr));
    }

    this._teilnahme = ref<Teilnahme>(null);
    if (localStorage.getItem(LocalStorageSelectedTeilnahme)!= null) {
      this._teilnahme.value = JSON.parse(localStorage.getItem(LocalStorageSelectedTeilnahme));
    }
  }

  get Datenjahr(): Ref<number> {
    return this._datenjahr;
  }

  get DatenjahrIsSet(): ComputedRef<boolean> {
    return computed(() => this._datenjahr.value != null && this._datenjahr.value > 0);
  }

  get DatenjahrIsLaufendesJahr(): ComputedRef<boolean> {
    return computed(() => true); //computed(() => this.DatenjahrIsSet && (this._datenjahr.value ?? 0) === new Date().getFullYear() - 1);
  }

  async SetDatenjahr(datenjahr: number, reloadPage: boolean) {
    this._datenjahr.value = datenjahr;
    localStorage.setItem(LocalStorageSelectedDatenjahr, JSON.stringify(datenjahr));

    let teilnahmen: Teilnahme[] = await this.Teilnahmen.ListAll();
    
    if (teilnahmen && teilnahmen.length > 0) {
      let latestTeilnahme: Teilnahme = teilnahmen.sort((a:Teilnahme, b:Teilnahme) => a.datenjahr! - b.datenjahr!).reverse()[0];
      this.SetTeilnahme(latestTeilnahme);
    }
    else {
      this.ClearTeilnahme();
    }

    if (reloadPage) {
      location.reload();
    }
  }

  ClearDatenjahr(): void {
    localStorage.removeItem(LocalStorageSelectedDatenjahr);
  }

  get Teilnahme(): Ref<Teilnahme> {
    return this._teilnahme;
  }

  get TeilnahmeIsSet(): ComputedRef<boolean> {
    return computed(() => this._teilnahme.value!= null);
  }

  SetTeilnahme(teilnahme: Teilnahme) {
    this._teilnahme.value = teilnahme;
    localStorage.setItem(LocalStorageSelectedTeilnahme, JSON.stringify(teilnahme));
  }

  ClearTeilnahme(): void {
    this._teilnahme.value = null;
    localStorage.removeItem(LocalStorageSelectedTeilnahme);
  }

  get Anmerkungen(): IDatenjahrItemRepository<Anmerkung> {
    return new DatenjahrItemRepository<Anmerkung>(this, 'anmerkungen', this._datenjahr.value);
  }

  get Abfallaufkommen(): IDatenjahrItemRepository<Abfallaufkommen> {
    return new DatenjahrItemRepository<Abfallaufkommen>(this, 'abfallaufkommen', this._datenjahr.value);
  }

  get Bezugsgroessen(): IDatenjahrItemRepository<KennzahlenGroesse> {
    return new DatenjahrItemRepository<KennzahlenGroesse>(this, 'bezugsgroesse', this._datenjahr.value);
  }

  get Zielgroessen(): IDatenjahrItemRepository<Zielgroesse> {
    return new DatenjahrItemRepository<Zielgroesse>(this, 'zielgroesse', this._datenjahr.value);
  }

  get Kennzahlen(): IDatenjahrItemRepository<Kennzahl> {
    return new DatenjahrItemRepository<Kennzahl>(this, 'kennzahl', this._datenjahr.value);
  }

  get ProdukteUndDl(): IDatenjahrItemRepository<ProduktDl> {
    return new DatenjahrItemRepository<ProduktDl>(this, 'produkteUndDienstleistungen', this._datenjahr.value);
  }

  get Einsatzstoffverwendungen(): IDatenjahrItemRepository<Einsatzstoffverwendung> {
    return new DatenjahrItemRepository<Einsatzstoffverwendung>(this, "einsatzstoffverwendung", this._datenjahr.value);
  }

  get Recycling(): IDatenjahrItemRepository<Recycling> {
    return new DatenjahrItemRepository<Recycling>(this, "recycling", this._datenjahr.value);
  }

  get Produktionsrueckstandsaufkommen(): IDatenjahrItemRepository<Produktionsrueckstandsaufkommen> {
    return new DatenjahrItemRepository<Produktionsrueckstandsaufkommen>(this, "produktionsrueckstandsaufkommen", this._datenjahr.value);
  }

  get Energie(): IDatenjahrItemRepository<Energie> {
    return new DatenjahrItemRepository<Energie>(this, "energie", this._datenjahr.value);
  }

  get Klimabilanz(): IDatenjahrItemRepository<Klimabilanz> {
    return new DatenjahrItemRepository<Klimabilanz>(this, "klimabilanz", this._datenjahr.value);
  }

  get ModalSplit(): IDatenjahrItemRepository<ModalSplit> {
    return new DatenjahrItemRepository<ModalSplit>(this, 'modalsplit', this._datenjahr.value);
  }

  get Massnahmen(): IDatenjahrItemRepository<Massnahme> {
    return new DatenjahrItemRepository<Massnahme>(this, "Umweltleistung", this._datenjahr.value);
  }

  get GroessteUmweltauswirkungen(): IDatenjahrItemRepository<GroessteUmweltauswirkungen> {
    return new DatenjahrItemRepository<GroessteUmweltauswirkungen>(this, "groessteUmweltauswirkungen", this._datenjahr.value);
  }

  get Umweltwirkungen(): IDatenjahrItemRepository<Umweltwirkung> {
    return new DatenjahrItemRepository<Umweltwirkung>(this, "Umweltwirkung", this._datenjahr.value);
  }
  
  get OekologischeReinigung(): IDatenjahrItemRepository<OekologischeReinigung> {
    return new DatenjahrItemRepository<OekologischeReinigung>(this, 'oekologischereinigung', this._datenjahr.value);
  }

  get Heizwaermebedarfe(): IDatenjahrItemRepository<Heizwaermebedarf> {
    return new DatenjahrItemRepository<Heizwaermebedarf>(this, 'heizwaermebedarf', this._datenjahr.value);
  }

  get Teilnahmen(): IDatenjahrItemRepository<Teilnahme> {
    return new DatenjahrItemRepository<Teilnahme>(this, 'teilnahme', this._datenjahr.value);
  }

  get SdgBewertungen(): IDatenjahrItemRepository<SdgBewertung> {
    return new DatenjahrItemRepository<SdgBewertung>(this, 'sdgbewertung', this._datenjahr.value);
  }

  get Stakeholder(): IDatenjahrItemRepository<Stakeholder> {
    return new DatenjahrItemRepository<Stakeholder>(this, 'stakeholder', this._datenjahr.value);
  }

  get Handlungsfelder(): IDatenjahrItemRepository<Handlungsfelder> {
    return new DatenjahrItemRepository<Handlungsfelder>(this, 'handlungsfeld', this._datenjahr.value);
  }

  get Lieferanten(): IDatenjahrItemRepository<Lieferant> {
    return new DatenjahrItemRepository<Lieferant>(this, 'lieferant', this._datenjahr.value);
  }

  get MitarbeiterStrukturen(): IDatenjahrItemRepository<MitarbeiterStruktur> {
    return new DatenjahrItemRepository<MitarbeiterStruktur>(this, 'mitarbeiterstruktur', this._datenjahr.value);
  }

  get Nachhaltigkeitsprogramme(): IDatenjahrItemRepository<Nachhaltigkeitsprogramm> {
    return new DatenjahrItemRepository<Nachhaltigkeitsprogramm>(this, 'nachhaltigkeitsprogramm', this._datenjahr.value);
  }

  get QChecks(): IDatenjahrItemRepository<QCheck> {
    return new DatenjahrItemRepository<QCheck>(this, 'qcheck', this._datenjahr.value);
  } 

  get AuditChecklisten(): IDatenjahrItemRepository<AuditCheckliste> {
    return new DatenjahrItemRepository<AuditCheckliste>(this, 'auditcheckliste', this._datenjahr.value);
  }

  get Wasserverbrauch(): IDatenjahrItemRepository<Wasserverbrauch> {
    return new DatenjahrItemRepository<Wasserverbrauch>(this, 'wasserverbrauch', this._datenjahr.value);
  }

  get Fragebogen(): IDatenjahrItemRepository<NachhaltigkeitsFrage> {
    return new DatenjahrItemRepository<NachhaltigkeitsFrage>(this, 'fragebogen', this._datenjahr.value);
  }

  async Protokoll(): Promise<LogItem[]> {
    return await this.Get<LogItem[]>('protokoll', true) as LogItem[];
  }

}